import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { HardDrive, ShoppingCart, Loader2, Cpu, Trash2, Plus } from "lucide-react";

const fulfillColor = {
  pending: "bg-amber-500/20 text-amber-300", confirmed: "bg-blue-500/20 text-blue-300",
  processing: "bg-blue-500/20 text-blue-300", shipped: "bg-emerald-500/20 text-emerald-300",
  delivered: "bg-emerald-500/20 text-emerald-300", cancelled: "bg-red-500/20 text-red-300",
};

export default function Hardware() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [buy, setBuy] = useState(null);
  const [qty, setQty] = useState(1);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);
  const [term, setTerm] = useState({ name: "", workplace_id: "" });
  const [tOpen, setTOpen] = useState(false);

  const load = async () => {
    const [p, o, t, w] = await Promise.all([
      api.get("/hardware/products"), api.get("/hardware/orders"), api.get("/terminals"), api.get("/workplaces"),
    ]);
    setProducts(p.data); setOrders(o.data); setTerminals(t.data); setWorkplaces(w.data);
  };
  useEffect(() => { load(); }, []);

  const placeOrder = async () => {
    setBusy(true);
    try { await api.post("/hardware/orders", { product_id: buy.id, quantity: qty, address }); toast.success("Order placed! Track it under Orders."); setBuy(null); setAddress(""); setQty(1); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const addTerminal = async () => {
    setBusy(true);
    try { const { data } = await api.post("/terminals", term); toast.success(`Terminal ${data.terminal_code} added. Pairing code: ${data.pairing_code}`); setTOpen(false); setTerm({ name: "", workplace_id: "" }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const unpair = async (t) => { await api.delete(`/terminals/${t.id}`); toast.success("Terminal unpaired."); load(); };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><HardDrive className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Workplace Hardware</h1></div>
      <Tabs defaultValue="store">
        <TabsList className="bg-[#0A0D0C] border border-emerald-500/15">
          <TabsTrigger value="store" data-testid="tab-store">Store</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="terminals" data-testid="tab-terminals">Terminals</TabsTrigger>
        </TabsList>

        <TabsContent value="store" className="mt-5">
          <p className="text-sm text-muted-foreground mb-4">Optional dedicated attendance terminals. You can also use any tablet or phone for free via Workplaces → Display QR.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {products.map((p) => (
              <div key={p.id} className="card-emerald p-6" data-testid="product-card">
                <Cpu className="w-8 h-8 text-emerald-400" />
                <h3 className="font-display font-semibold mt-3">{p.name}</h3>
                <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-display text-2xl font-extrabold">${p.price}</span>
                  <Button onClick={() => setBuy(p)} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="order-product-btn"><ShoppingCart className="w-4 h-4 mr-1" /> Order</Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          {orders.length === 0 ? <p className="text-muted-foreground">No hardware orders yet.</p> : (
            <div className="space-y-3">
              {orders.map((o) => (
                <div key={o.id} className="card-emerald p-5 flex flex-wrap items-center justify-between gap-3" data-testid="order-row">
                  <div><p className="font-medium">{o.quantity}× {o.product_name}</p><p className="text-xs text-muted-foreground">{o.id} · {o.address}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-semibold">${o.amount}</span>
                    <Badge className={fulfillColor[o.fulfillment_status] || "bg-white/10"}>{o.fulfillment_status}</Badge>
                    {o.tracking_number && <span className="text-xs text-emerald-300">Track: {o.tracking_number}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="terminals" className="mt-5">
          <div className="flex justify-end mb-3">
            <Dialog open={tOpen} onOpenChange={setTOpen}>
              <DialogTrigger asChild><Button variant="outline" className="border-emerald-500/40" data-testid="add-terminal-btn"><Plus className="w-4 h-4 mr-1" /> Pair terminal</Button></DialogTrigger>
              <DialogContent className="bg-[#121816] border-emerald-500/20">
                <DialogHeader><DialogTitle>Pair a terminal</DialogTitle><DialogDescription>Assign a QwikLogs terminal to one of your workplaces.</DialogDescription></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Terminal name</Label><Input value={term.name} onChange={(e) => setTerm({ ...term, name: e.target.value })} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="terminal-name-input" /></div>
                  <div><Label>Workplace</Label>
                    <Select value={term.workplace_id} onValueChange={(v) => setTerm({ ...term, workplace_id: v })}>
                      <SelectTrigger className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="terminal-workplace-select"><SelectValue placeholder="Select workplace" /></SelectTrigger>
                      <SelectContent className="bg-[#121816] border-emerald-500/20">{workplaces.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={addTerminal} disabled={busy || !term.name || !term.workplace_id} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="terminal-save-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pair"}</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {terminals.length === 0 ? <p className="text-muted-foreground">No terminals paired. Use any device via Workplaces → Display QR, or order hardware.</p> : (
            <div className="space-y-3">
              {terminals.map((t) => (
                <div key={t.id} className="card-emerald p-5 flex items-center justify-between" data-testid="terminal-row">
                  <div><p className="font-medium">{t.name} <span className="text-xs text-muted-foreground">({t.terminal_code})</span></p><p className="text-xs text-muted-foreground">{t.workplace_name}</p></div>
                  <div className="flex items-center gap-3">
                    <Badge className={t.status === "online" ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-muted-foreground"}>{t.status}</Badge>
                    <Button size="sm" variant="ghost" onClick={() => unpair(t)} data-testid="unpair-terminal-btn"><Trash2 className="w-4 h-4 text-red-400" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!buy} onOpenChange={(v) => !v && setBuy(null)}>
        <DialogContent className="bg-[#121816] border-emerald-500/20">
          <DialogHeader><DialogTitle>Order {buy?.name}</DialogTitle><DialogDescription>Enter quantity and delivery address. Payment status starts as pending.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div><Label>Quantity</Label><Input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="order-qty-input" /></div>
            <div><Label>Delivery address</Label><Input value={address} onChange={(e) => setAddress(e.target.value)} className="mt-1.5 bg-[#0A0D0C] border-emerald-500/20" data-testid="order-address-input" /></div>
            <p className="text-sm text-muted-foreground">Total: <span className="text-emerald-300 font-semibold">${((buy?.price || 0) * qty).toFixed(2)}</span></p>
          </div>
          <DialogFooter><Button onClick={placeOrder} disabled={busy || !address} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="place-order-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Place order"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
